import { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence, type Variants } from 'motion/react'
import { Header } from '../components/Header'
import {
  getGesturelyPricePreview,
  initPaddle,
  isPaddleConfigurationValid,
  openGesturelyCheckout,
} from '../config/paddle'
import styles from '../styles/buy.module.css'

// ── Types ───────────────────────────────────────────────────────

type PageState = 'idle' | 'loading' | 'ready' | 'opening' | 'success' | 'error'

// Bezier curves typed as const tuples for Motion
const EASE_EXPO: [number, number, number, number] = [0.16, 1, 0.3, 1]
const EASE_IN_OUT: [number, number, number, number] = [0.45, 0, 0.55, 1]

// ── Animation Variants ──────────────────────────────────────────

const pageVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: EASE_EXPO },
  },
  exit: { opacity: 0, y: -12, transition: { duration: 0.25, ease: EASE_IN_OUT } },
}

const successVariants: Variants = {
  hidden: { opacity: 0, scale: 0.96, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.55, ease: EASE_EXPO },
  },
}

// ── Benefit Item ────────────────────────────────────────────────

interface BenefitProps {
  icon: string
  text: string
}

function Benefit({ icon, text }: BenefitProps) {
  return (
    <li className={styles.benefit}>
      <span className={styles.benefitIcon} aria-hidden="true">{icon}</span>
      <span>{text}</span>
    </li>
  )
}

// ── Buy Page ────────────────────────────────────────────────────

export function BuyPage() {
  // Preflight: validate env config before the first render so an unavailable
  // checkout does not flash a loading state or require a synchronous effect update.
  const isConfigured = isPaddleConfigurationValid()
  const [pageState, setPageState] = useState<PageState>(
    isConfigured ? 'loading' : 'error'
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(
    isConfigured
      ? null
      : 'Checkout is not available at this time. Please try again later or contact support.'
  )
  const initAttempted = useRef(false)
  const [displayPrice, setDisplayPrice] = useState('$14.99')
  const [displayCurrency, setDisplayCurrency] = useState('USD')

  // Initialize Paddle on mount
  useEffect(() => {
    if (initAttempted.current) return
    initAttempted.current = true

    if (!isConfigured) return

    initPaddle()
      .then((paddle) => {
        if (paddle) {
          setPageState('ready')
          getGesturelyPricePreview().then((preview) => {
            if (!preview) return
            setDisplayPrice(preview.formattedTotal)
            setDisplayCurrency(preview.currencyCode)
          })
        } else {
          setPageState('error')
          setErrorMessage('Checkout could not be initialized. Please refresh and try again.')
        }
      })
      .catch(() => {
        setPageState('error')
        setErrorMessage('Checkout could not be initialized. Please refresh and try again.')
      })
  }, [isConfigured])

  const handleBuy = useCallback(() => {
    if (pageState !== 'ready') return

    setPageState('opening')
    setErrorMessage(null)

    openGesturelyCheckout(
      // onComplete — UI feedback only. License delivered server-side via webhook.
      () => {
        setPageState('success')
      },
      // onError
      (_err: Error) => {
        setPageState('ready')
        setErrorMessage(
          'The checkout could not be opened. Please try again or contact support@getgesturely.com.'
        )
      },
      // onClose — buyer dismissed checkout without completing payment.
      () => setPageState('ready')
    ).catch(() => {
      setPageState('ready')
      setErrorMessage('Something went wrong. Please try again.')
    })
  }, [pageState])

  const isButtonDisabled = pageState === 'loading' || pageState === 'opening' || pageState === 'success'

  const buttonLabel = (() => {
    if (pageState === 'loading') return 'Loading…'
    if (pageState === 'opening') return 'Opening checkout…'
    return `Buy Gesturely — ${displayPrice}`
  })()

  return (
    <div className={styles.page}>
      <Header />

      <main className={styles.main} id="main-content">
        <AnimatePresence mode="wait">
          {pageState === 'success' ? (
            // ── Success State ──────────────────────────────────────────────
            <motion.div
              key="success"
              className={styles.successWrapper}
              variants={successVariants}
              initial="hidden"
              animate="visible"
            >
              <div className={styles.successCard}>
                <div className={styles.successIcon} aria-hidden="true">
                  <CheckCircleIcon />
                </div>
                <h1 className={styles.successTitle}>Purchase complete.</h1>
                <p className={styles.successSubtitle}>
                  Your Gesturely license is being sent to your email.
                </p>
                <p className={styles.successNote}>
                  Delivery usually takes only a moment. If you don't see it, check
                  your spam folder or use{' '}
                  <strong>Restore Purchase</strong> in Gesturely.
                </p>
                <div className={styles.successDivider} role="separator" />
                <p className={styles.successHelp}>
                  Need help?{' '}
                  <a href="mailto:support@getgesturely.com">
                    support@getgesturely.com
                  </a>
                </p>
              </div>
            </motion.div>
          ) : (
            // ── Checkout State ─────────────────────────────────────────────
            <motion.div
              key="checkout"
              className={styles.checkoutWrapper}
              variants={pageVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {/* Eyebrow */}
              <p className={styles.eyebrow}>One-time purchase</p>

              {/* Headline */}
              <h1 className={styles.headline}>
                Make your trackpad{' '}
                <span className={styles.headlineEmphasis}>yours.</span>
              </h1>

              {/* Subheading */}
              <p className={styles.subheading}>
                Unlock Gesturely on up to 2 personal Macs with one purchase.
              </p>

              {/* Price Card */}
              <div className={styles.priceCard}>
                {/* Price Display */}
                <div className={styles.priceRow}>
                  <span className={styles.price}>{displayPrice}</span>
                  <span className={styles.priceNote}>{displayCurrency}, one-time</span>
                </div>

                {/* CTA Button */}
                <motion.button
                  className={styles.buyButton}
                  onClick={handleBuy}
                  disabled={isButtonDisabled}
                  aria-disabled={isButtonDisabled}
                  aria-label={buttonLabel}
                  whileTap={!isButtonDisabled ? { scale: 0.97 } : undefined}
                  transition={{ duration: 0.12, ease: [0.16, 1, 0.3, 1] }}
                >
                  <span className={styles.buyButtonContent}>
                    {(pageState === 'loading' || pageState === 'opening') && (
                      <span className={styles.spinner} aria-hidden="true" />
                    )}
                    {buttonLabel}
                  </span>
                </motion.button>

                {/* Error state */}
                <AnimatePresence>
                  {errorMessage && (
                    <motion.p
                      className={styles.errorMessage}
                      role="alert"
                      aria-live="polite"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                    >
                      {errorMessage}
                    </motion.p>
                  )}
                </AnimatePresence>

                {/* Benefits List */}
                <ul className={styles.benefits} aria-label="What's included">
                  <Benefit icon="✓" text="One-time purchase — no subscription" />
                  <Benefit icon="✓" text="Up to 2 personal Macs" />
                  <Benefit icon="✓" text="14-day full-feature trial in the app" />
                  <Benefit icon="✓" text="License delivered by email" />
                  <Benefit icon="✓" text="Secure checkout powered by Paddle" />
                </ul>

                {/* Payment methods note */}
                <p className={styles.paymentNote}>
                  Cards, Apple Pay, Google Pay, PayPal, and eligible local payment
                  methods are handled securely by Paddle.
                </p>
              </div>

              {/* Already purchased */}
              <div className={styles.restoreSection}>
                <p className={styles.restoreTitle}>Already purchased?</p>
                <p className={styles.restoreText}>
                  Open Gesturely → Settings → <strong>Restore Purchase</strong>
                </p>
              </div>

              {/* Support */}
              <p className={styles.supportLink}>
                Questions?{' '}
                <a href="mailto:support@getgesturely.com">
                  support@getgesturely.com
                </a>
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className={styles.footer} role="contentinfo">
        <div className={`container ${styles.footerInner}`}>
          <p className={styles.footerCopy}>
            © {new Date().getFullYear()} Gesturely. All rights reserved.
          </p>
          <p className={styles.footerPaddle}>
            Payment processing by{' '}
            <a
              href="https://paddle.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Paddle
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}

// ── Icon Components ─────────────────────────────────────────────

function CheckCircleIcon() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="24" cy="24" r="24" fill="var(--color-success-bg)" />
      <circle cx="24" cy="24" r="16" stroke="var(--color-success)" strokeWidth="1.5" fill="none" />
      <path
        d="M17 24.5l5 5 9-9"
        stroke="var(--color-success)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
